import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { Translate, TextFormat, getSortState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT, APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC, SORT } from 'app/shared/util/pagination.constants';
import { overrideSortStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './blog-table.reducer';

export const BlogTable = () => {
  const dispatch = useAppDispatch();

  const location = useLocation();
  const navigate = useNavigate();

  const [sortState, setSortState] = useState(overrideSortStateWithQueryParams(getSortState(location, 'id'), location.search));

  const blogTableList = useAppSelector(state => state.blogTable.entities);
  const loading = useAppSelector(state => state.blogTable.loading);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        sort: `${sortState.sort},${sortState.order}`,
      })
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?sort=${sortState.sort},${sortState.order}`;
    if (location.search !== endURL) {
      navigate(`${location.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [sortState.order, sortState.sort]);

  const sort = p => () => {
    setSortState({
      ...sortState,
      order: sortState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = sortState.sort;
    const order = sortState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    } else {
      return order === ASC ? faSortUp : faSortDown;
    }
  };

  return (
    <div>
      <h2 id="blog-table-heading" data-cy="BlogTableHeading">
        <Translate contentKey="reachamyApp.blogTable.home.title">Blog Tables</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="reachamyApp.blogTable.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/blog-table/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="reachamyApp.blogTable.home.createLabel">Create new Blog Table</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {blogTableList && blogTableList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="reachamyApp.blogTable.id">ID</Translate> <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('title')}>
                  <Translate contentKey="reachamyApp.blogTable.title">Title</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('title')} />
                </th>
                <th className="hand" onClick={sort('blogContent')}>
                  <Translate contentKey="reachamyApp.blogTable.blogContent">Blog Content</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('blogContent')} />
                </th>
                <th className="hand" onClick={sort('categoryId')}>
                  <Translate contentKey="reachamyApp.blogTable.categoryId">Category Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('categoryId')} />
                </th>
                <th className="hand" onClick={sort('imageId')}>
                  <Translate contentKey="reachamyApp.blogTable.imageId">Image Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('imageId')} />
                </th>
                <th className="hand" onClick={sort('postAsNgo')}>
                  <Translate contentKey="reachamyApp.blogTable.postAsNgo">Post As Ngo</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('postAsNgo')} />
                </th>
                <th className="hand" onClick={sort('ngoUserId')}>
                  <Translate contentKey="reachamyApp.blogTable.ngoUserId">Ngo User Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('ngoUserId')} />
                </th>
                <th className="hand" onClick={sort('createdBy')}>
                  <Translate contentKey="reachamyApp.blogTable.createdBy">Created By</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('createdBy')} />
                </th>
                <th className="hand" onClick={sort('createdAt')}>
                  <Translate contentKey="reachamyApp.blogTable.createdAt">Created At</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('createdAt')} />
                </th>
                <th className="hand" onClick={sort('modifiedBy')}>
                  <Translate contentKey="reachamyApp.blogTable.modifiedBy">Modified By</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('modifiedBy')} />
                </th>
                <th className="hand" onClick={sort('modifiedAt')}>
                  <Translate contentKey="reachamyApp.blogTable.modifiedAt">Modified At</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('modifiedAt')} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {blogTableList.map((blogTable, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/blog-table/${blogTable.id}`} color="link" size="sm">
                      {blogTable.id}
                    </Button>
                  </td>
                  <td>{blogTable.title}</td>
                  <td>{blogTable.blogContent}</td>
                  <td>{blogTable.categoryId}</td>
                  <td>{blogTable.imageId}</td>
                  <td>{blogTable.postAsNgo ? 'true' : 'false'}</td>
                  <td>{blogTable.ngoUserId}</td>
                  <td>{blogTable.createdBy}</td>
                  <td>{blogTable.createdAt ? <TextFormat type="date" value={blogTable.createdAt} format={APP_DATE_FORMAT} /> : null}</td>
                  <td>{blogTable.modifiedBy}</td>
                  <td>{blogTable.modifiedAt ? <TextFormat type="date" value={blogTable.modifiedAt} format={APP_DATE_FORMAT} /> : null}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button tag={Link} to={`/blog-table/${blogTable.id}`} color="info" size="sm" data-cy="entityDetailsButton">
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button tag={Link} to={`/blog-table/${blogTable.id}/edit`} color="primary" size="sm" data-cy="entityEditButton">
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button tag={Link} to={`/blog-table/${blogTable.id}/delete`} color="danger" size="sm" data-cy="entityDeleteButton">
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="reachamyApp.blogTable.home.notFound">No Blog Tables found</Translate>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default BlogTable;
